import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Grid from '@material-ui/core/Grid';
import { Typography, makeStyles } from '@material-ui/core';
import {
  ClientsHistoryMonitorGraph,
  PeriodCountAccess,
  UsersHistoryMonitorGraph,
} from 'modules/monitor/interfaces';
import AutoCard from 'components/AutoCard';

import {
  setPeriodClientHistoryFilter,
  getClientsHistoryMonitorGraphRequest,
  getUsersHistoryMonitorGraphRequest,
} from 'modules/monitor/actions';
import FilterSelector from 'components/FilterSelector';
import { formatPercent } from 'utils/helpers';
import StyledTypography from 'components/StyledTypography';
import styles, { overrideCard, overrideFilter } from './styles';

export enum MonitorCardsEnum {
  environmentMissing = 'environmentMissing',
  environmentBlock = 'environmentBlock',
  instanceProblem = 'instanceProblem',
}

interface Props {
  clientsHistoryMonitorGraphFields: ClientsHistoryMonitorGraph;
  usersHistoryMonitorGraphFields: UsersHistoryMonitorGraph;
  intervalTime: any;
  setPeriodClientHistoryFilter: (prop: string) => void;
  getClientsHistoryMonitorGraphRequest: ({ intervalTime }) => void;
  getUsersHistoryMonitorGraphRequest: ({ intervalTime }) => void;
}

const ClientsHistory = ({
  clientsHistoryMonitorGraphFields,
  usersHistoryMonitorGraphFields,
  intervalTime = '30d',
  setPeriodClientHistoryFilter,
  getClientsHistoryMonitorGraphRequest,
  getUsersHistoryMonitorGraphRequest,
}: Props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles({});

  const filterPeriodOptions = () => [
    {
      id: 'last-7-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_7_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_7_DAYS),
    },
    {
      id: 'last-15-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_15_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_15_DAYS),
    },
    {
      id: 'last-30-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_30_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_30_DAYS),
    },
    {
      id: 'last-60-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_60_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_60_DAYS),
    },
    {
      id: 'last-90-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_90_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_90_DAYS),
    },
    {
      id: 'last-180-days',
      label: i18n.t(
        `ENVIRONMENT_DASHBOARD.LAST_${PeriodCountAccess.LAST_180_DAYS.toUpperCase()}`
      ),
      onClick: () =>
        setPeriodClientHistoryFilter(PeriodCountAccess.LAST_180_DAYS),
    },
  ];

  React.useEffect(() => {
    getClientsHistoryMonitorGraphRequest({ intervalTime });
  }, [getClientsHistoryMonitorGraphRequest, intervalTime]);

  React.useEffect(() => {
    getUsersHistoryMonitorGraphRequest({ intervalTime });
  }, [getUsersHistoryMonitorGraphRequest, intervalTime]);

  return (
    <>
      <AutoCard overrideClasses={overrideCard()}>
        <Grid item justify="flex-start" className={classes.values}>
          <Grid
            container
            alignItems="center"
            justify="flex-start"
            className={classes.filter}
          >
            <FilterSelector
              overrideClasses={overrideFilter}
              options={filterPeriodOptions()}
              currentValue={{
                label: i18n.t(
                  `ENVIRONMENT_DASHBOARD.LAST_${intervalTime.toUpperCase()}`
                ),
                onClick: () => null,
              }}
            />
          </Grid>
          <Grid item xs={10} className={classes.content}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Typography className={classes.title}>
                  {i18n.t('CREATED_CLIENTS_KEY')}
                </Typography>
              </Grid>

              <Grid item>
                <StyledTypography
                  value={
                    clientsHistoryMonitorGraphFields?.created_clients_in_period
                      .count
                  }
                />
              </Grid>
            </Grid>

            <Typography className={classes.subtitle}>
              {i18n.t('CREATED_CLIENTS_BY_PERIOD_KEY', {
                percentage: formatPercent(
                  clientsHistoryMonitorGraphFields?.created_clients_in_period
                    .growth_in_period_percent
                ),
              })}
            </Typography>
          </Grid>
          <Grid item xs={10} className={classes.content}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Typography className={classes.title}>
                  {i18n.t('CREATED_USERS_KEY')}
                </Typography>
              </Grid>

              <Grid item>
                <StyledTypography
                  value={
                    usersHistoryMonitorGraphFields?.created_users_in_period
                      .count
                  }
                />
              </Grid>
            </Grid>

            <Typography className={classes.subtitle}>
              {i18n.t('CREATED_CLIENTS_BY_PERIOD_KEY', {
                percentage: formatPercent(
                  usersHistoryMonitorGraphFields?.created_users_in_period
                    .growth_in_period_percent
                ),
              })}
            </Typography>
          </Grid>
        </Grid>
      </AutoCard>
    </>
  );
};

const mapStateToProps = ({
  monitor: {
    clientsHistoryMonitorGraphFields,
    usersHistoryMonitorGraphFields,
    periodFilterHistory,
  },
}: ApplicationState) => ({
  clientsHistoryMonitorGraphFields,
  usersHistoryMonitorGraphFields,
  intervalTime: periodFilterHistory,
});

export default connect(mapStateToProps, {
  setPeriodClientHistoryFilter,
  getClientsHistoryMonitorGraphRequest,
  getUsersHistoryMonitorGraphRequest,
})(ClientsHistory);
